import { RefreshCw } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ApiError, API_BASE } from '@/api/http'
import { useAuthStore } from '@/stores/authStore'

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAuthStore((s) => s.login)
  const status = useAuthStore((s) => s.status)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [captchaImageUrl, setCaptchaImageUrl] = useState('')
  const [captchaSalt, setCaptchaSalt] = useState('')
  const [loading, setLoading] = useState(false)
  const [captchaSecondsLeft, setCaptchaSecondsLeft] = useState(0)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const blobUrlRef = useRef('')
  const captchaImageRef = useRef<HTMLImageElement | null>(null)

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }

  const clearCountdownTimer = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }

  // 验证码码 = HMAC(secret, unix/60), 同一 60s 窗口内固定不变(无状态设计)。
  // 倒计时让用户明白这是时间窗口码, 到点自动换, 而非每次点刷新就换(同 Invoice)。
  const startCountdown = (expiresAt: number) => {
    clearCountdownTimer()
    const tick = () => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
      setCaptchaSecondsLeft(left)
      if (left <= 0) clearCountdownTimer()
    }
    tick()
    countdownTimerRef.current = setInterval(tick, 1000)
  }

  const scheduleCaptchaRefresh = (expiresAt: number) => {
    clearRefreshTimer()
    const delayMs = Math.max(1000, expiresAt - Date.now() + 200)
    refreshTimerRef.current = setTimeout(() => {
      void refreshCaptcha()
    }, delayMs)
  }

  const refreshCaptcha = async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/auth/captcha?t=${Date.now()}`, {
        credentials: 'include',
      })
      const enabled = response.headers.get('X-Captcha-Enabled') === 'true'
      setCaptchaEnabled(enabled)
      if (!response.ok || !enabled) {
        setCaptchaImageUrl('')
        setCaptchaSalt('')
        return
      }
      setCaptchaSalt(response.headers.get('X-Captcha-Salt') ?? '')

      const svg = await response.text()
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      const objectURL = URL.createObjectURL(blob)
      blobUrlRef.current = objectURL
      setCaptchaImageUrl(objectURL)

      const expiresAt = response.headers.get('X-Captcha-Expires-At')
      if (expiresAt) {
        const expiresMs = Date.parse(expiresAt)
        if (!Number.isNaN(expiresMs)) {
          scheduleCaptchaRefresh(expiresMs)
          startCountdown(expiresMs)
        }
      }
    } catch (error) {
      setCaptchaImageUrl('')
      setCaptchaEnabled(false)
      if (error instanceof Error) {
        console.error('refresh captcha failed', error)
      }
    }
  }

  useEffect(() => {
    void refreshCaptcha()
    return () => {
      clearRefreshTimer()
      clearCountdownTimer()
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      if (captchaImageRef.current) {
        captchaImageRef.current.src = ''
      }
    }
  }, [])

  useEffect(() => {
    if (status === 'authed') navigate(from, { replace: true })
  }, [status, navigate, from])

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!username.trim() || !password) {
      toast.error(t('login.required'))
      return
    }
    setLoading(true)
    try {
      if (captchaEnabled && !captcha.trim()) {
        toast.error(t('login.captchaPlaceholder'))
        return
      }
      await login(username.trim(), password, captcha.trim(), captchaSalt)
      navigate(from, { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.code === 'invalid_captcha') {
        toast.error(t('errors.codes.invalid_captcha'))
        setCaptcha('')
        void refreshCaptcha()
      } else {
        toast.error(t('login.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-[390px]">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="nf-pulse size-3 rounded-full bg-primary" aria-hidden />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary">{t('app.name')}</p>
              <p className="truncate text-xs text-muted-foreground">{t('app.tagline')}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
        <Card className="rounded-lg border-border/80 bg-card/95 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={onSubmit}>
              <div className="space-y-2">
                <Label htmlFor="username">{t('login.username')}</Label>
                <Input
                  id="username"
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={loading}
                />
              </div>
              {captchaEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="captcha">{t('login.captchaLabel')}</Label>
                    <Button
                      className="h-8 px-2"
                      disabled={loading}
                      onClick={() => {
                        void refreshCaptcha()
                      }}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <RefreshCw className="mr-2 size-4" />
                      {t('login.captchaRefresh')}
                    </Button>
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    {captchaImageUrl ? (
                      <div className="flex items-center gap-2">
                        <img
                          ref={captchaImageRef}
                          alt="captcha"
                          className="h-11 rounded border border-input bg-muted px-2"
                          src={captchaImageUrl}
                        />
                        {captchaSecondsLeft > 0 && (
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {captchaSecondsLeft}s
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex h-11 items-center justify-center rounded border border-dashed border-input bg-muted px-2 text-xs text-muted-foreground">
                        captcha
                      </div>
                    )}
                    <Input
                      id="captcha"
                      inputMode="numeric"
                      maxLength={6}
                      onChange={(event) => setCaptcha(event.target.value)}
                      placeholder={t('login.captchaPlaceholder')}
                      value={captcha}
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
              <Button className="w-full" size="lg" type="submit" disabled={loading}>
                {loading ? t('login.submitting') : t('login.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
